import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../styles/profile.css";
import "../styles/profile-modal.css";

import { useNavigate } from "react-router-dom";
import { apiFetch } from "../utils/api";
import { BASE_URLS } from "../config/urls";
import { useAuth } from "../context/AuthContext";
import {UserProfile} from "../types/form";
import {EditProfileModal} from "../components/modal/EditProfileModal";
import {ChangePasswordModal} from "../components/modal/ChangePasswordModal";
import {CompaniesModal} from "../components/modal/CompaniesModal";
import {Company} from "../models/Company";


const ProfilePage: React.FC = () => {
    const { logout, isAdmin } = useAuth();
    const navigate = useNavigate();

    const [profile, setProfile] = useState<UserProfile>({ name: "—", email: "—" });

    const [isProfileModalOpen, setProfileModalOpen] = useState(false); // 1) edit profile
    const [isPasswordModalOpen, setPasswordModalOpen] = useState(false); // 2) change password
    const [isCompaniesModalOpen, setCompaniesModalOpen] = useState(false); // 3) add company

    const [isCompaniesPopupVisible, setCompaniesPopupVisible] = useState(false);

    const [loading, setLoading] = useState(true);
    const [companies, setCompanies] = useState<Company[]>([]);

    // --- загрузка данных пользователя ---
    useEffect(() => {
        let cancelled = false;

        apiFetch(`${BASE_URLS.api}/personal_account`)
            .then((resp) => resp.json())
            .then((data) => {
                if (cancelled) return;

                const hasNoCompanies = !data.companies || data.companies.length === 0;

                const fullName = data.name; // как у тебя

                setProfile({
                    name: fullName || "Пользователь",
                    email: (data.email ?? "—").trim() || "—",
                });

                const shouldShowCompanies = hasNoCompanies && !isAdmin;
                setCompaniesPopupVisible(shouldShowCompanies);
                setCompanies(Array.isArray(data?.companies) ? data.companies : []);

                // ✅ Авто-открытие модалки компаний при загрузке
                if (shouldShowCompanies) {
                    setCompaniesModalOpen(true);
                }
            })
            .catch(() => {
                // можно показать тост/ошибку по желанию
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [isAdmin]);

    const avatarLetter = useMemo(() => {
        const s = (profile.name || "").trim();
        return (s ? s[0] : "U").toUpperCase();
    }, [profile.name]);

    const handleLogout = useCallback(async () => {
        try {
            await apiFetch(`${BASE_URLS.api}/logout`, { method: "POST" });
        } catch (e) {
            console.error("Logout error:", e);
        }
        logout();
        window.location.assign(BASE_URLS.app + "/login");
    }, [logout]);

    // handlers открытия модалок
    const openEditProfile = () => setProfileModalOpen(true);
    const openCompanies = () => setCompaniesModalOpen(true);

    const refresh = useCallback(async () => {
        try {
            const resp = await apiFetch(`${BASE_URLS.api}/personal_account`);
            if (!resp.ok) return;
            const data = await resp.json();

            setCompanies(Array.isArray(data?.companies) ? data.companies : []);
            // по желанию можно обновить профиль тоже:
            setProfile({
                name: data.name || "Пользователь",
                email: (data.email ?? "—").trim() || "—",
            });
        } catch (e) {
            console.error(e);
        }
    }, []);


    return (
        <div className="profile-page">
            <div className="profile-grid">
                {/* LEFT: user card */}
                <div className="profile-card profile-card--user">
                    <div className="profile-badge">Личный кабинет</div>

                    <div className="profile-user">
                        <div className="profile-avatar" aria-hidden="true">
                            {avatarLetter}
                        </div>

                        <div className="profile-user-meta">
                            <div className="profile-name">{loading ? "Загрузка…" : profile.name}</div>
                            <div className="profile-email">{loading ? "—" : profile.email}</div>
                        </div>
                    </div>

                    <div className="profile-user-actions">
                        <button className="profile-secondary-btn" onClick={openEditProfile} disabled={loading}>
                            Изменить
                        </button>

                        <button className="profile-danger-btn" onClick={handleLogout} disabled={loading}>
                            Выйти
                        </button>
                    </div>
                </div>

                {/* RIGHT TOP: orders */}
                <div className="profile-card profile-card--actions">
                    <div className="profile-card-head">
                        <div className="profile-card-title">Записи</div>
                    </div>

                    <div className="profile-actions-grid">
                        <button className="profile-primary-tile" onClick={() => navigate("/orders/current")} disabled={loading}>
                            <div className="profile-tile-title">Текущие поставки</div>
                            <div className="profile-tile-desc">Активные записи</div>
                        </button>

                        <button className="profile-secondary-tile" onClick={() => navigate("/orders/history")} disabled={loading}>
                            <div className="profile-tile-title">История заказов</div>
                            <div className="profile-tile-desc">Все записи</div>
                        </button>
                    </div>
                </div>

                {/* RIGHT BOTTOM: companies */}
                <div className="profile-card profile-card--actions">
                    <div className="profile-card-head">
                        <div className="profile-card-title">Компании</div>
                    </div>

                    <div className="profile-actions-grid">
                        <button className="profile-primary-tile" onClick={openCompanies} disabled={loading}>
                            <div className="profile-tile-title">Мои компании</div>
                            <div className="profile-tile-desc">Добавить / управлять</div>
                        </button>
                    </div>
                </div>
            </div>

            {/* ===== MODALS ===== */}

            <EditProfileModal
                isOpen={isProfileModalOpen}
                initial={profile}
                onClose={() => setProfileModalOpen(false)}
                onSaved={(next) => setProfile(next)}
                onOpenPassword={() => {
                    setProfileModalOpen(false);
                    setPasswordModalOpen(true);
                }}
            />

            <ChangePasswordModal
                isOpen={isPasswordModalOpen}
                onClose={() => setPasswordModalOpen(false)}
            />

            <CompaniesModal
                isOpen={isCompaniesModalOpen}
                onClose={() => {
                    setCompaniesModalOpen(false);
                    setCompaniesPopupVisible(false);
                }}
                autoOpened={isCompaniesPopupVisible}
                companies={companies}
                onCompaniesChanged={refresh}
            />
        </div>
    );
};

export default ProfilePage;
